/**
 * Per-email preview status. Mirrors the server's `BulkInviteValidateStatus` — the
 * bulk-invite validate endpoint is the single source of truth for parsing + validation.
 */
export enum BulkInviteEmailStatus {
  /** Well-formed, not a duplicate, and not already in the org — will be invited. */
  Valid = 'valid',
  /** Not a valid email address. */
  InvalidFormat = 'invalid_format',
  /** A case-insensitive duplicate of an earlier row in the same file. */
  Duplicate = 'duplicate',
  /** Already an active member of this organization. */
  AlreadyActive = 'already_active',
  /** Already invited to this organization and pending acceptance. */
  Pending = 'pending',
}

/** One row of the upload preview: the email as returned by the server plus its status. */
export interface BulkInvitePreviewRow {
  /** The email as it appeared in the file. */
  email: string;
  /** The server-computed preview status. */
  status: BulkInviteEmailStatus;
}

/**
 * A validate/dispatch failure that belongs to the role/scope selection, not the file.
 * Throw it from `onValidateFile`/`onBulkSubmit` when the message is already rendered by the
 * owner of `bulkAccessFields`: the dialog still treats the attempt as failed (no preview,
 * stays open) but suppresses its own inline message so the error shows once, on the picker.
 * Without a `bulkAccessFields` slot there is nowhere else to render it, so the dialog falls
 * back to showing the message inline.
 */
export class BulkInviteGrantError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BulkInviteGrantError';
    Object.setPrototypeOf(this, BulkInviteGrantError.prototype);
  }
}

/**
 * Name-brand check for `BulkInviteGrantError`. Used instead of `instanceof` because the throw
 * (business hook) and the check (this dialog) live in different packages: a duplicated module
 * instance (second bundle copy, version skew, test transform) would make `instanceof` fail
 * silently and leak grant errors into the file-level message slot.
 */
export function isBulkInviteGrantError(error: unknown): error is BulkInviteGrantError {
  return error instanceof Error && error.name === 'BulkInviteGrantError';
}
